import cx from 'classnames';
import React, { MouseEvent, useMemo } from 'react';
import styled from '@emotion/styled';
import { Button, ButtonVariant } from '@vimily/bonjoro-ui-components';
import { Assignee } from '~/types/api/nextgen';
import { theme } from '@vimily/bonjoro-ui-components-v2';

const Profile = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 5px 1px;
  border-bottom: 1px solid #eee;
`;

const ProfileImgContainer = styled.div`
  height: 68px;
  padding: 10px;
`;

const ProfileImg = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 6px;
`;

const ProfileInfo = styled.div`
  flex: 1 1 auto;
`;

const Name = styled.div`
  font-size: 16px;
  line-height: 1.2em;
  font-weight: bold;
  margin-bottom: 2px;
  word-break: break-all;
`;

const CompanyName = styled.div`
  line-height: 1.2em;
  font-size: 14px;
  letter-spacing: 0.007rem;
`;

export interface SidebarProfileProps extends Omit<Assignee, 'locale'> {
  className?: string;
  showBackButton?: boolean;
  onBackClick?: () => void;
  onProfileImageClick?: () => void;
  fontFamily?: string;
  fontColor?: string 
}

export default function SidebarProfile({
  avatar,
  first_name,
  last_name,
  bio,
  showBackButton,
  onBackClick,
  onProfileImageClick,
  className,
  fontFamily,
  fontColor = theme.colors.neutral[900],
}: SidebarProfileProps) {
  const genericFontStyle = { color: fontColor, fontFamily };

  const name = useMemo(() => {
    let name = 'Bonjoro user';
    if (first_name != null) {
      name = first_name;
      if (last_name != null) {
        name = `${name} ${last_name}`;
      }

      name = name.trim();
    }

    return name;
  }, [first_name, last_name]);

  const handleBackClick = (e: MouseEvent) => {
    e.stopPropagation();
    onBackClick?.();
  };

  return (
    <Profile
      className={cx('sidebar-profile', className)}
      onClick={onProfileImageClick}
    >
      {showBackButton && (
        <Button variant={ButtonVariant.Icon} onClick={handleBackClick}>
          <i className='fas fa-chevron-left text-neutral-800 text-lg' />
        </Button>
      )}
      <ProfileImgContainer className='profile-img cursor-pointer'>
        <ProfileImg src={avatar} alt='Profile Image' />
      </ProfileImgContainer>
      <ProfileInfo className='profile-info'>
        <Name className='info-name' style={genericFontStyle}>{name}</Name>
        <CompanyName className='info-company' style={genericFontStyle}>{bio}</CompanyName>
      </ProfileInfo>
    </Profile>
  );
}
